import React, { useEffect, useState, useCallback } from "react";
import { Clock, ChevronDown, ChevronUp } from "lucide-react";
import { Skeleton, ErrorState } from "../ui/LoadingStates";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

function ratingColor(r) {
  const n = parseFloat(r);
  if (n >= 4.5) return "#00ff90";
  if (n >= 3.5) return "#60a5fa";
  if (n >= 2.5) return "#fbbf24";
  return "#f87171";
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// "2026-01" → "January 2026", "2025-10" → "October 2025".
// Falls through to the raw string for anything we can't parse.
function fmtPeriod(period) {
  if (!period) return "—";
  const s = String(period);
  const ym = s.match(/^(\d{4})-(\d{1,2})$/);
  if (ym) {
    const m = Number(ym[2]) - 1;
    if (m >= 0 && m < 12) return `${MONTH_NAMES[m]} ${ym[1]}`;
  }
  const ymd = s.match(/^(\d{4})-(\d{1,2})-\d{1,2}$/);
  if (ymd) {
    const m = Number(ymd[2]) - 1;
    if (m >= 0 && m < 12) return `${MONTH_NAMES[m]} ${ymd[1]}`;
  }
  return s;
}

function typeBadge(t) {
  const v = String(t || "").toLowerCase();
  if (v.includes("manager"))  return { label: "Manager", color: "#60a5fa" };
  if (v.includes("peer"))     return { label: "Peer",    color: "#c084fc" };
  if (v.includes("self"))     return { label: "Self",    color: "#fbbf24" };
  return null;
}

export default function PerformanceHistory() {
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }
    setLoading(true); setError(null);
    fetch(`${API_BASE}/api/performance/history`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setHistory(Array.isArray(d) ? d : []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
        <Clock size={14} className="text-[#00ff90]/60" />
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Performance History</span>
        {!loading && history.length > 0 && (
          <span className="ml-auto text-xs text-white/30">{history.length} review{history.length !== 1 ? "s" : ""}</span>
        )}
      </div>

      <div className="p-5">
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[1,2,3].map(i => (
              <div key={i} className="rounded-lg border border-white/5 bg-white/3 p-4"
                   style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <Skeleton width={48} height={48} radius={12} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                  <Skeleton width="40%" height={12} />
                  <Skeleton width="90%" height={10} />
                  <Skeleton width="60%" height={10} />
                </div>
              </div>
            ))}
          </div>
        )}
        {error && <ErrorState message="Couldn't load performance history" detail={error} onRetry={load} />}

        {!loading && !error && history.length === 0 && (
          <p className="text-white/20 text-sm italic text-center py-6">No past reviews available.</p>
        )}

        {!loading && !error && history.length > 0 && (
          <div className="space-y-3">
            {history.map((item, i) => {
              const id      = item.id ?? i;
              const color   = ratingColor(item.rating);
              const rating  = parseFloat(item.rating);
              const open    = expanded === id;
              const badge   = typeBadge(item.type || item.review_type);
              return (
                <div key={id} className="rounded-lg border border-white/5 bg-white/3 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setExpanded(open ? null : id)}
                    className="w-full text-left p-4 flex items-start gap-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-xl border flex items-center justify-center shrink-0"
                      style={{ borderColor: `${color}40`, background: `${color}10` }}>
                      <span className="text-sm font-bold" style={{ color }}>
                        {isNaN(rating) ? "—" : rating.toFixed(1)}
                      </span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-white">{fmtPeriod(item.period)}</span>
                        {badge && (
                          <span className="text-xs border px-2 py-0.5 rounded-full"
                            style={{ color: badge.color, borderColor: `${badge.color}55`, background: `${badge.color}15` }}>
                            {badge.label}
                          </span>
                        )}
                        {item.cycle && (
                          <span className="text-xs text-white/25 border border-white/10 px-2 py-0.5 rounded-full">
                            {item.cycle}
                          </span>
                        )}
                      </div>
                      {item.manager_name && (
                        <p className="text-xs text-white/30 mt-1">Reviewed by {item.manager_name}</p>
                      )}
                    </div>

                    <span className="text-white/30 shrink-0 mt-1">
                      {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </span>
                  </button>

                  {open && (
                    <div className="px-4 pb-4 -mt-1 border-t border-white/5 text-sm text-white/70 space-y-2">
                      {item.achievements && (
                        <div>
                          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mt-3 mb-1">Achievements</p>
                          <p className="text-white/80 whitespace-pre-line">{item.achievements}</p>
                        </div>
                      )}
                      {item.challenges && (
                        <div>
                          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mt-3 mb-1">Challenges</p>
                          <p className="text-white/80 whitespace-pre-line">{item.challenges}</p>
                        </div>
                      )}
                      {!isNaN(rating) && (
                        <div>
                          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mt-3 mb-1">Rating</p>
                          <p className="text-white/80">{rating.toFixed(1)} / 5</p>
                        </div>
                      )}
                      {item.comments && (
                        <div>
                          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mt-3 mb-1">Comments</p>
                          <p className="text-white/80 whitespace-pre-line">{item.comments}</p>
                        </div>
                      )}
                      {item.goals_discussed && (
                        <div>
                          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mt-3 mb-1">Goals discussed</p>
                          <p className="text-white/80 whitespace-pre-line">{item.goals_discussed}</p>
                        </div>
                      )}
                      {item.goals_completed != null && item.goals_total != null && (
                        <p className="text-xs text-white/40 mt-3">
                          Goals: {item.goals_completed}/{item.goals_total} completed
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
